import { useCallback, useMemo } from 'react'

import { MultiSelect } from 'govwell-ui'
import { SelectOption } from 'govwell-ui/components/Select/types'
import { FieldTemplateForViewFragment } from 'types/graphql'

import { useAllFieldTemplatesInRecordTypeQuery } from 'src/fetch/recordTypes'
import { dedupeObjectsById, filterNil } from 'src/utils'

type Props = {
  onChange: (values: FieldTemplateForViewFragment[] | undefined) => void
  value: FieldTemplateForViewFragment[] | undefined
  recordTypeId: number | undefined
}

const SharedFieldGroupLabel = 'Shared Field Groups'

const FieldTemplateMultiSelect = ({ onChange, value, recordTypeId }: Props) => {
  const { data, isLoading } = useAllFieldTemplatesInRecordTypeQuery(recordTypeId)
  const fieldType = value?.[0]?.type

  const options: SelectOption<FieldTemplateForViewFragment>[] = useMemo(() => {
    const fields = filterNil(
      data?.recordType?.recordTemplates?.flatMap((rt) =>
        rt.applicationFields
          ?.filter((f) => !fieldType || f.type === fieldType)
          ?.map((f) => {
            const group = f.fieldGroupTemplate?.isSharedFieldGroup
              ? SharedFieldGroupLabel
              : `${rt.name || 'No Name'}`
            return {
              id: f.id,
              group,
              label: `${group}: ${f.label}`,
              customDisplay: (v: FieldTemplateForViewFragment) => v.label,
              value: f,
            }
          })
      ) ?? []
    )
    const dedupedFields: SelectOption<FieldTemplateForViewFragment>[] = dedupeObjectsById(
      fields
    ).map((f) => ({
      group: f.group,
      label: f.label,
      value: f.value,
      customDisplay: (v: FieldTemplateForViewFragment) => v.label,
    }))

    for (const field of value ?? []) {
      if (field.isArchived) {
        const groupName = field.fieldGroupTemplate?.isSharedFieldGroup
          ? SharedFieldGroupLabel
          : field.fieldGroupTemplate?.usedInRecordTemplates?.[0]?.name || 'No Name'
        dedupedFields.unshift({
          group: groupName,
          label: `[ARCHIVED] ${groupName}: ${field.label}`,
          customDisplay: (v: FieldTemplateForViewFragment) => `[ARCHIVED] ${v.label}`,
          value: field,
        })
      }
    }

    const sortedFields = dedupedFields.sort((a, b) => {
      if (a.group !== b.group) {
        // We want to show shared field group fields first
        if (a.group === SharedFieldGroupLabel) {
          return -1
        }
        if (b.group === SharedFieldGroupLabel) {
          return 1
        }

        return (a.group || '').localeCompare(b.group || '')
      }

      return (a.label || '').localeCompare(b.label || '')
    })

    return sortedFields
  }, [data?.recordType?.recordTemplates, fieldType, value])
  const optionsById = useMemo(
    () =>
      new Map<number, SelectOption<FieldTemplateForViewFragment>>(
        options.map((o) => [o.value.id, o])
      ),
    [options]
  )
  const selectedOptions = useMemo(
    () => filterNil(value?.map((v) => optionsById.get(v.id)) ?? []),
    [optionsById, value]
  )

  const getOptionKey = useCallback(
    (o: SelectOption<FieldTemplateForViewFragment>) => o.value.id,
    []
  )

  const handleSelectedOptionsChange = useCallback(
    (newSelectedOptions: SelectOption<FieldTemplateForViewFragment>[] | undefined) => {
      onChange(newSelectedOptions?.map((o) => o.value))
    },
    [onChange]
  )

  return (
    <MultiSelect<FieldTemplateForViewFragment>
      caption="All fields selected must share the same field type."
      getOptionKey={getOptionKey}
      isDisabled={isLoading}
      isLoading={isLoading}
      isRequired
      label="Fields to Display"
      onSelectedOptionsChange={handleSelectedOptionsChange}
      options={options}
      placeholder={isLoading ? 'Loading fields...' : undefined}
      selectedOptions={selectedOptions}
    />
  )
}

export default React.memo(FieldTemplateMultiSelect)
